import { Col, Divider, Image, Row, Steps, Tag, Tooltip } from "antd";
import {
  DATETIME_FORMAT_FRONTEND,
  TIME_FORMAT_BACKEND,
  TIME_FORMAT_FRONTEND
} from "configs/constants";
import { showInfo } from "helper/util";
import { replaceElementInArray, showDate } from "helper/util";
import { random } from "lodash";
import MapBoxJobDetail from "modules/shared/PickupDropoff/mapbox/MapBoxJobDetail";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { sumBy } from "lodash";
import OrdersHeader from "../components/header/OrdersHeader";
import { selectJobOrder } from "../store/orderSlice";
import { doGetJobOrder } from "../store/thunks";
import { validationApi } from "../../validations/store/api";
import FailedStopMap from "modules/shared/FailedStopMap";
import clientBillingApi from "modules/client/billing/store/api";
import routeApi from "modules/client/routes/store/api";
import adminClientApi from "modules/admin/clients/store/api";
import "./JobDetail.scss";

const SFTP_POD_ENTITLEMENT_ID = "d38eafe1-6679-4d6b-8161-38b34f9bec2a";

const JobDetail = ({ isAdmin }) => {
  const defaultCenter = {
    lat: 18.4224299,
    lng: -66.107137
  };

  // Calculate GPS proximity between driver location and destination
  const calculateGPSProximity = (driverLocation, destination) => {
    const latDiff = Math.abs(Number(driverLocation.lat) - Number(destination.lat));
    const lngDiff = Math.abs(Number(driverLocation.lng) - Number(destination.lng));

    const EXACT_THRESHOLD = 0.0003; // ~30 meters
    const CLOSE_THRESHOLD = 0.0025; // ~250 meters

    if (latDiff <= EXACT_THRESHOLD && lngDiff <= EXACT_THRESHOLD) {
      return "exact";
    } else if (latDiff <= CLOSE_THRESHOLD && lngDiff <= CLOSE_THRESHOLD) {
      return "close";
    } else {
      return "far";
    }
  };

  const [markers, setMarkers] = useState([]);
  const dispatch = useDispatch();
  const [step, setStep] = useState(0);
  const job = useSelector(selectJobOrder);
  const [pickup, setPickup] = useState({});
  const [routePickup, setRoutePickup] = useState({});
  const [dropoff, setDropoff] = useState({});
  const [routeDropoff, setRouteDropoff] = useState({});
  const [pickupBarcodes, setPickupBarcodes] = useState([]);
  const [dropoffBarcodes, setDropoffBarcodes] = useState([]);
  const params = useParams();
  const [packsByType, setPackByType] = useState({});
  const createdAt = pickup?.createdAt
    ? moment(pickup?.createdAt).format(DATETIME_FORMAT_FRONTEND)
    : "";
  const packages = job?.packages || [];
  const routesUri = isAdmin ? "/admin/routes" : "/routes";
  const breadCrumbs = [
    { text: "ACTIVE ROUTES", url: routesUri },
    { text: params.id?.substr(0, 8).toUpperCase(), url: `${routesUri}/${params.id}` },
    { text: params.eid?.substr(0, 8).toUpperCase() }
  ];

  // Document Scanners
  const [pickupDocuments, setPickupDocuments] = useState([]);
  const [dropoffDocuments, setDropoffDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Validation settings
  const [validationSettings, setValidationSettings] = useState([]);
  const [validationsLoading, setValidationsLoading] = useState(false);

  // SFTP POD upload log
  const [sftpPodEntitlementActive, setSftpPodEntitlementActive] = useState(false);
  // undefined = not yet fetched, null = no log found, object = log record
  const [sftpPodLog, setSftpPodLog] = useState(undefined);

  useEffect(() => {
    if (params.eid) {
      dispatch(doGetJobOrder(params.eid));
    }
  }, [dispatch, params]);

  // Check SFTP POD entitlement — admin checks the route's client, client checks own entitlement
  useEffect(() => {
    const checkEntitlement = async (fetchFn) => {
      try {
        const data = await fetchFn();
        const ent = (data || []).find((e) => e.id === SFTP_POD_ENTITLEMENT_ID);
        setSftpPodEntitlementActive(ent?.client_is_active === true);
      } catch {
        // no-op
      }
    };

    if (!isAdmin) {
      checkEntitlement(() => clientBillingApi.getClientEntitlements());
      return;
    }

    // Admin: params.id is the route ID — fetch the route to get its clientId
    if (!params.id) return;
    routeApi
      .getRoute(params.id)
      .then((r) => {
        if (r?.clientId) {
          checkEntitlement(() => adminClientApi.getClientEntitlements(r.clientId));
        }
      })
      .catch(() => {});
  }, [isAdmin, params.id]);

  // Fetch the last SFTP upload log once the job is confirmed completed and entitlement is active
  useEffect(() => {
    if (job?.status === "completed" && sftpPodEntitlementActive && params.eid) {
      routeApi
        .getSftpPodLog(params.eid)
        .then((data) => setSftpPodLog(data ?? null))
        .catch(() => setSftpPodLog(null));
    }
  }, [job?.status, sftpPodEntitlementActive, params.eid]);

  const handleDataMapbox = (jobParams) => {
    if (jobParams?.routeLocations) {
      const allowKeys = ["arrivedAt", "completedAt"];
      let tmpMarkers = [];
      jobParams.routeLocations.map((routeLocation) => {
        tmpMarkers = [
          ...tmpMarkers,
          {
            id: random(1000000),
            lat: Number(routeLocation.location.lat),
            lng: Number(routeLocation.location.lng),
            type: `delivery_${routeLocation.location.type}`
          }
        ];
        const groupStatusDate = routeLocation?.groupStatusDate;
        const keysStatus = Object.keys(groupStatusDate);
        keysStatus.map((key) => {
          if (allowKeys.includes(key)) {
            tmpMarkers = [
              ...tmpMarkers,
              {
                id: random(1000000),
                lat: Number(groupStatusDate[key].lat),
                lng: Number(groupStatusDate[key].lng),
                type: `${key}_${routeLocation.location.type}`
              }
            ];
          }
        });
      });
      setMarkers(tmpMarkers);
    }
  };

  useEffect(() => {
    if (job) {
      const stepJob = {
        unassigned: 0,
        assigned: 1,
        pickup_arrived: 2,
        collected: 3,
        dropoff_arrived: 4,
        completed: 5,
        failed: 6
      };
      const locations = job.locations || [];
      const routes = job.routeLocations || [];
      setPickup(locations.find((location) => location.type === "pickup") || {});
      setRoutePickup(routes.find((route) => route.location.type === "pickup") || {});
      setDropoff(locations.find((location) => location.type === "dropoff") || {});
      setRouteDropoff(routes.find((route) => route.location.type === "dropoff") || {});
      const previousStep = Object.keys(job.groupStatusDate || {}).length;
      setStep(stepJob[job?.status] || previousStep - 1);
      handleDataMapbox(job);

      /* 2025-06-23 - Fetch Document Scanners
      They are a separate join / relation of "RouteLocation" data, not included here even in the
      job.routeLocations. Classic Job vs RouteLocation issue. We must fetch these and ready to
      display. Because of S3, unlike images we can't preview or display inline, so when you click
      it will download. This is server side header of application type, we do NOT control that.
      */

      // get jwt for fetch calls - should be a standard helper
      const getTokenFromCookie = () => {
        const cookies = document.cookie.split(";");
        const tokenCookie = cookies.find((cookie) =>
          cookie.trim().startsWith("flexio-token=")
        );
        return tokenCookie ? tokenCookie.split("=")[1] : null;
      };
      const token = getTokenFromCookie();

      // Get the route location IDs from the current job data
      const pickupRouteLocationId = routes.find(
        (route) => route.location.type === "pickup"
      )?.id;
      const dropoffRouteLocationId = routes.find(
        (route) => route.location.type === "dropoff"
      )?.id;

      // simple promise fetch to get docu scanners
      const fetchDocumentScanners = async () => {
        // early exit if not defined
        if (!pickupRouteLocationId && !dropoffRouteLocationId) return;

        setLoading(true);

        try {
          const promises = [];

          if (pickupRouteLocationId) {
            promises.push(
              fetch(
                `${window.REACT_APP_API_ENDPOINT}/mobile/route-locations/${pickupRouteLocationId}`,
                {
                  method: "GET",
                  headers: {
                    accept: "application/json",
                    Authorization: `Bearer ${token}`
                  }
                }
              )
                .then((res) => res.json())
                .then((data) => ({
                  type: "pickup",
                  documents: data.documentScanners || []
                }))
            );
          }

          if (dropoffRouteLocationId) {
            promises.push(
              fetch(
                `${window.REACT_APP_API_ENDPOINT}/mobile/route-locations/${dropoffRouteLocationId}`,
                {
                  method: "GET",
                  headers: {
                    accept: "application/json",
                    Authorization: `Bearer ${token}`
                  }
                }
              )
                .then((res) => res.json())
                .then((data) => ({
                  type: "dropoff",
                  documents: data.documentScanners || []
                }))
            );
          }

          const results = await Promise.all(promises);

          results.forEach((result) => {
            if (result.type === "pickup") {
              setPickupDocuments(result.documents);
            } else if (result.type === "dropoff") {
              setDropoffDocuments(result.documents);
            }
          });
        } catch (error) {
          console.error("Error fetching document scanners:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchDocumentScanners();

      // Fetch validation settings
      const fetchValidationSettings = async () => {
        setValidationsLoading(true);
        try {
          const settings = await validationApi.getAll();
          setValidationSettings(settings);
        } catch (error) {
          console.error("Error fetching validation settings:", error);
        } finally {
          setValidationsLoading(false);
        }
      };

      fetchValidationSettings();
    }
  }, [job]);

  /* Docuscanner: Function to handle click (download) - note - I was NOT able to get
  this to display on the page, apparently due to S3 headers which define the behaviour.
  I tried a few tricks as you can see, still just downloads file.
  */
  const handleDocumentClick = (url) => {
    // Force inline display
    const displayUrl = `${url}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`;
    window.open(displayUrl, "_blank");
  };

  const DocumentIcon = () => (
    <div
      style={{
        width: "100px",
        height: "100px",
        border: "4px solid #06f",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        backgroundColor: "#f9f9f9",
        margin: "5px",
        fontSize: "36px"
      }}
    >
      📄
    </div>
  );

  useEffect(() => {
    const rs = {};
    const pickupBarcodesT = [];
    const dropoffBarcodesT = [];

    job?.packages?.forEach((pack) => {
      rs[pack?.packageType?.name] = rs[pack?.packageType?.name] || [];
      rs[pack?.packageType?.name].push(pack);
    });
    if (job?.routeLocations?.length > 0) {
      job?.routeLocations.forEach((routeLocation) => {
        if (routeLocation?.routeLocationPackages?.length > 0) {
          routeLocation?.routeLocationPackages.forEach((locationPackage) => {
            if (locationPackage?.package?.packageType?.name) {
              const tmpPackages = rs[locationPackage?.package?.packageType?.name] || [];
              const findPack = tmpPackages.find(
                (p) => p.id === locationPackage?.package?.id
              );
              if (findPack) {
                const newPackages = replaceElementInArray(tmpPackages, {
                  ...findPack,
                  [`${routeLocation?.location?.type}Quantity`]: locationPackage?.quantity,
                  validationMethod: locationPackage?.validationMethod
                });
                if (
                  routeLocation?.location?.type === "pickup" &&
                  locationPackage?.validationMethod === "scan"
                ) {
                  pickupBarcodesT.push({
                    id: findPack?.id,
                    name: findPack?.name,
                    quantity: findPack?.quantity,
                    scanned: locationPackage?.quantity
                  });
                }
                if (
                  routeLocation?.location?.type === "dropoff" &&
                  locationPackage?.validationMethod === "scan"
                ) {
                  dropoffBarcodesT.push({
                    id: findPack?.id,
                    name: findPack?.name,
                    quantity: findPack?.quantity,
                    scanned: locationPackage?.quantity
                  });
                }
                rs[locationPackage?.package?.packageType?.name] = newPackages;
              }
            }
          });
        }
      });
    }

    setPickupBarcodes(pickupBarcodesT);
    setDropoffBarcodes(dropoffBarcodesT);
    setPackByType(rs);
  }, [job]);

  const { Step } = Steps;

  // 2025-07-15 - Bug, Pickup Waiting Time (pickup.duration) is 0, using waitingTime instead

  // don't use momentJs diff, it doesn't work without "date", it's overly complex for simple math
  const calculateWaitingTime = (startTime, endTime) => {
    // early return - validation due to react mount lifecycle tries this before pickup defined
    if (
      !startTime ||
      !endTime ||
      typeof startTime !== "string" ||
      typeof endTime !== "string"
    ) {
      return 0;
    }

    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const [endHours, endMinutes] = endTime.split(":").map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;

    return endTotalMinutes - startTotalMinutes;
  };

  const waitingTime = calculateWaitingTime(pickup.arrivalTime, pickup.finishTime);

  // Helper function to get barcode scanner status
  const getBarcodeStatus = (locationType, barcodes) => {
    const fieldName = `${locationType}_barcodeScanner`;
    const validation = validationSettings.find((v) => v.fieldName === fieldName);

    // If barcode scanning was actually performed, show SCANNED regardless of settings
    const totalScanned = sumBy(barcodes, "scanned");
    if (totalScanned > 0) {
      return { status: "scanned", text: "SCANNED", className: "scanned" };
    }

    // Check validation settings for requirement status
    if (!validation || !validation.enabled) {
      return { status: "disabled", text: "Not Required", className: "disabled" };
    }

    if (validation.required) {
      return { status: "required", text: "Required", className: "required" };
    } else {
      return { status: "optional", text: "Optional", className: "optional" };
    }
  };

  const pickupBarcodeStatus = getBarcodeStatus("pickup", pickupBarcodes);
  const dropoffBarcodeStatus = getBarcodeStatus("delivery", dropoffBarcodes);

  return (
    <section className="modules active-orders pages job-detail">
      <OrdersHeader breadCrumbs={breadCrumbs} hasButton={false} />
      <Row gutter={25} className="wrapper">
        <Col span="4">
          <div className="left steps-job">
            <Steps progressDot current={step} direction="vertical">
              <Step
                title={<Tag className="unassigned">Unassigned</Tag>}
                description={showDate(job?.groupStatusDate?.unassignedAt)}
              />
              <Step
                title={<Tag className="assigned">Assigned</Tag>}
                description={showDate(job?.groupStatusDate?.assignedAt)}
              />
              <Step
                title={<Tag className="assigned">Arrived at Pickup Location</Tag>}
                description={showDate(job?.groupStatusDate?.pickupArriveAt)}
              />
              <Step
                title={<Tag className="collected">Collected</Tag>}
                description={showDate(job?.groupStatusDate?.collectedAt)}
              />
              <Step
                title={<Tag className="assigned">Arrived at Dropoff Location</Tag>}
                description={showDate(job?.groupStatusDate?.dropoffArriveAt)}
              />
              <Step
                title={<Tag className="completed">Completed</Tag>}
                description={showDate(job?.groupStatusDate?.completedAt)}
              />
              <Step
                title={<Tag className="failed">Failed</Tag>}
                description={
                  <>
                    {showDate(job?.groupStatusDate?.failedAt)}
                    {routePickup?.groupStatusDate?.failedAt?.lat &&
                      routePickup?.groupStatusDate?.failedAt?.lng &&
                      pickup?.lat &&
                      pickup?.lng && (
                        <>
                          <FailedStopMap
                            driverLocation={{
                              lat: routePickup.groupStatusDate.failedAt.lat,
                              lng: routePickup.groupStatusDate.failedAt.lng
                            }}
                            dropoffLocation={{
                              lat: pickup.lat,
                              lng: pickup.lng
                            }}
                            locationType="pickup"
                          />
                          <div style={{ marginTop: 8 }}>
                            <Tooltip
                              title={
                                <div>
                                  <div>
                                    <strong>GPS Proximity Calculation:</strong>
                                  </div>
                                  <div>
                                    • <strong>Exact:</strong> Within 0.000300 GPS points
                                    (~30 meters)
                                  </div>
                                  <div>
                                    • <strong>Close:</strong> Within 0.002500 GPS points
                                    (~250 meters)
                                  </div>
                                  <div>
                                    • <strong>Far:</strong> More than 0.002500 GPS points
                                    away
                                  </div>
                                </div>
                              }
                            >
                              <span style={{ cursor: "help", fontSize: "12px" }}>
                                Driver GPS at Failure:{" "}
                                <strong>
                                  {calculateGPSProximity(
                                    {
                                      lat: routePickup.groupStatusDate.failedAt.lat,
                                      lng: routePickup.groupStatusDate.failedAt.lng
                                    },
                                    {
                                      lat: pickup.lat,
                                      lng: pickup.lng
                                    }
                                  )}
                                </strong>
                              </span>
                            </Tooltip>
                          </div>
                        </>
                      )}
                    {routeDropoff?.groupStatusDate?.failedAt?.lat &&
                      routeDropoff?.groupStatusDate?.failedAt?.lng &&
                      dropoff?.lat &&
                      dropoff?.lng && (
                        <>
                          <FailedStopMap
                            driverLocation={{
                              lat: routeDropoff.groupStatusDate.failedAt.lat,
                              lng: routeDropoff.groupStatusDate.failedAt.lng
                            }}
                            dropoffLocation={{
                              lat: dropoff.lat,
                              lng: dropoff.lng
                            }}
                            locationType="dropoff"
                          />
                          <div style={{ marginTop: 8 }}>
                            <Tooltip
                              title={
                                <div>
                                  <div>
                                    <strong>GPS Proximity Calculation:</strong>
                                  </div>
                                  <div>
                                    • <strong>Exact:</strong> Within 0.000300 GPS points
                                    (~30 meters)
                                  </div>
                                  <div>
                                    • <strong>Close:</strong> Within 0.002500 GPS points
                                    (~250 meters)
                                  </div>
                                  <div>
                                    • <strong>Far:</strong> More than 0.002500 GPS points
                                    away
                                  </div>
                                </div>
                              }
                            >
                              <span style={{ cursor: "help", fontSize: "12px" }}>
                                Driver GPS at Failure:{" "}
                                <strong>
                                  {calculateGPSProximity(
                                    {
                                      lat: routeDropoff.groupStatusDate.failedAt.lat,
                                      lng: routeDropoff.groupStatusDate.failedAt.lng
                                    },
                                    {
                                      lat: dropoff.lat,
                                      lng: dropoff.lng
                                    }
                                  )}
                                </strong>
                              </span>
                            </Tooltip>
                          </div>
                        </>
                      )}
                  </>
                }
              />
            </Steps>
          </div>
        </Col>
        <Col span="20">
          <div className="right">
            <div className="right-block addresses">
              <Row>
                <Col span="12">
                  <div className="address-info">
                    <div className="header">
                      <h3>PICKUP LOCATION</h3>
                    </div>
                    <div className="info address">
                      <label>Address</label>
                      {pickup.locationName && <p>{pickup.locationName}</p>}
                      <p>{pickup.address}</p>
                    </div>
                    <div className="info">
                      <label>Sender</label>
                      <p>{pickup.contactName}</p>
                      <p>{pickup.email}m</p>
                      {pickup.phoneList?.map((phone, i) => (
                        <p style={{ textTransform: "capitalize" }} key={i}>
                          {phone.type}: {phone.phone}
                        </p>
                      ))}
                    </div>
                    <div className="info">
                      <label>Pickup Time Window</label>
                      <p>
                        {pickup.start
                          ? moment(pickup.start, TIME_FORMAT_BACKEND).format(
                              TIME_FORMAT_FRONTEND
                            )
                          : ""}{" "}
                        -{" "}
                        {pickup.end
                          ? moment(pickup.end, TIME_FORMAT_BACKEND).format(
                              TIME_FORMAT_FRONTEND
                            )
                          : ""}
                      </p>
                    </div>
                    <div className="info">
                      <label>Est. Waiting/Service Time</label>
                      <p>{waitingTime} minutes</p>
                    </div>
                    <div className="info">
                      <label>Note</label>
                      <p>{showInfo(pickup.note)}</p>
                    </div>
                    <div className="info">
                      <label>Image/s</label>
                      <div className="images">
                        {pickup.attachments?.map((image) => (
                          <div className="image" key={image.id}>
                            <img src={image.url} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="info">
                      <label>Internal Route Id</label>
                      <p>{showInfo(pickup?.internalRouteId)}</p>
                    </div>
                    <div className="info">
                      <label>Internal Order Id</label>
                      <p>{showInfo(pickup?.internalOrderId)}</p>
                    </div>
                    <div className="info">
                      <label>Internal Customer Id</label>
                      <p>{showInfo(pickup?.internalCustomerId)}</p>
                    </div>
                    <div className="info">
                      <label>Salesperson</label>
                      <p>{showInfo(pickup?.salesPerson)}</p>
                    </div>
                    <div className="info">
                      <label>COD</label>
                      <p>{showInfo(pickup?.cod)}</p>
                    </div>
                    <div className="info">
                      <label>Failed Reason</label>
                      <p>{showInfo(routePickup?.groupStatusDate?.failedAt?.note)}</p>
                    </div>
                  </div>
                </Col>
                <Col span="12">
                  <div className="address-info">
                    <div className="header">
                      <h3>DROP-OFF LOCATION</h3>
                    </div>
                    <div className="info address">
                      <label>Address</label>
                      {dropoff.locationName && <p>{dropoff?.locationName} </p>}
                      <p>{dropoff.address}</p>
                    </div>
                    <div className="info">
                      <label>Sender</label>
                      <p>{dropoff.contactName}</p>
                      <p>{dropoff.email}</p>
                      {dropoff.phoneList?.map((phone, i) => (
                        <p style={{ textTransform: "capitalize" }} key={i + "drop"}>
                          {phone.type}: {phone.phone}
                        </p>
                      ))}
                    </div>
                    <div className="info">
                      <label>Drop-Off Time Window</label>
                      <p>
                        {dropoff.start
                          ? moment(dropoff.start, TIME_FORMAT_BACKEND).format(
                              TIME_FORMAT_FRONTEND
                            )
                          : ""}{" "}
                        -{" "}
                        {dropoff.end
                          ? moment(dropoff.end, TIME_FORMAT_BACKEND).format(
                              TIME_FORMAT_FRONTEND
                            )
                          : ""}
                      </p>
                    </div>
                    <div className="info">
                      <label>Est. Waiting/Service Time</label>
                      <p>{dropoff.duration} minutes</p>
                    </div>
                    <div className="info">
                      <label>Note</label>
                      <p>{showInfo(dropoff.note)}</p>
                    </div>
                    <div className="info">
                      <label>Image/s</label>
                      <div className="images">
                        {dropoff.attachments?.map((image) => (
                          <div className="image" key={image.id}>
                            <img src={image.url} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="info">
                      <label>Internal Route Id</label>
                      <p>{showInfo(dropoff?.internalRouteId)}</p>
                    </div>
                    <div className="info">
                      <label>Internal Order Id</label>
                      <p>{showInfo(dropoff?.internalOrderId)}</p>
                    </div>
                    <div className="info">
                      <label>Internal Customer Id</label>
                      <p>{showInfo(dropoff?.internalCustomerId)}</p>
                    </div>
                    <div className="info">
                      <label>Salesperson</label>
                      <p>{showInfo(dropoff?.salesPerson)}</p>
                    </div>
                    <div className="info">
                      <label>COD</label>
                      <p>{showInfo(dropoff?.cod)}</p>
                    </div>
                    <div className="info">
                      <label>Failed Reason</label>
                      <p>{showInfo(routeDropoff?.groupStatusDate?.failedAt?.note)}</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <div className="right-block packages-block">
              <div className="header">
                <h3>PACKAGE LIST</h3>
                <div className="count">{packages?.length || 0}</div>
              </div>
              <div className="content">
                <div className="packages-by-type">
                  {Object.keys(packsByType).length > 0 &&
                    Object.keys(packsByType).map((key, i) => (
                      <div key={key}>
                        <div className="packages-by-type">
                          <div className="type">
                            {key === undefined && (
                              <>
                                <b style={{ textTransform: "capitalize" }}>
                                  {key} Package
                                </b>
                              </>
                            )}
                          </div>
                          {packsByType[key].map((pack, iP) => (
                            <React.Fragment key={iP}>
                              <div className="package" key={pack.id}>
                                {pack?.name && (
                                  <>
                                    {iP + 1}. {pack.name} x {pack.quantity}{" "}
                                  </>
                                )}
                                {pack?.packageV2 ? (
                                  ""
                                ) : (
                                  <>{pack?.barcode ? `(${pack?.barcode})` : ""}</>
                                )}
                                {pack?.packageV2 && (
                                  <div>
                                    Pool Package{" "}
                                    {pack?.packageV2 ? `${pack?.packageV2.id}` : ""}
                                  </div>
                                )}
                              </div>
                              <ul>
                                <li>
                                  {" "}
                                  {`Pickup: ${pack?.pickupQuantity || 0}/ ${
                                    pack.quantity
                                  } (${showInfo(pack?.validationMethod)})`}{" "}
                                </li>
                                <li>
                                  {`Dropoff: ${pack?.dropoffQuantity || 0}/ ${
                                    pack.quantity
                                  } (${showInfo(pack?.validationMethod)})`}{" "}
                                </li>
                              </ul>
                            </React.Fragment>
                          ))}
                        </div>
                        {i !== Object.keys(packsByType).length - 1 && <Divider />}
                      </div>
                    ))}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div style={{ width: "100%" }}>
                <MapBoxJobDetail
                  height={544}
                  stops={markers}
                  width="100%"
                  center={defaultCenter}
                />
              </div>
            </div>
            <div className="right-block route-block">
              <Row gutter={25}>
                <Col span="12">
                  <div className="item pod">
                    <div className="header">
                      <h3>POP {routePickup.proofs2?.length > 0 && "1"}</h3>
                    </div>
                    <div className="content">
                      {routePickup.proofs?.length > 0 ? (
                        routePickup.proofs.map((proof) => (
                          <Image key={proof?.id} width={100} src={proof?.url} />
                        ))
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  {routePickup.proofs2?.length > 0 && (
                    <div className="item pod">
                      <div className="header">
                        <h3>POP 2</h3>
                      </div>
                      <div className="content">
                        {routePickup.proofs2.map((proof) => (
                          <Image key={proof?.id} width={100} src={proof?.url} />
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="item signature">
                    <div className="header">
                      <h3>SIGNATURE {routePickup.signaturePhoto2 && "1"}</h3>
                    </div>
                    <div className="content">
                      {routePickup?.signaturePhoto ? (
                        <>
                          <img src={routePickup.signaturePhoto.url} />
                          <div>
                            Signature Name:{" "}
                            {routePickup?.senderName || "(No name was provided)"}
                          </div>
                        </>
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  {routePickup.signaturePhoto2 && (
                    <div className="item signature">
                      <div className="header">
                        <h3>SIGNATURE 2</h3>
                      </div>
                      <div className="content">
                        <img src={routePickup.signaturePhoto2.url} />
                        <div>
                          Signature Name 2:{" "}
                          {routePickup?.senderName || "(No name was provided)"}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="item notes customer-notes">
                    <div className="header">
                      <h3>CUSTOMER NOTES</h3>
                    </div>
                    <div className="content">
                      {routePickup?.location?.note ? (
                        <div>{routePickup?.location?.note}</div>
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  <div className="item document-scanner">
                    <div className="header">
                      <h3>DOCUMENT SCANNER</h3>
                    </div>
                    <div className="content">
                      {loading ? (
                        <div>Loading documents...</div>
                      ) : pickupDocuments.length > 0 ? (
                        pickupDocuments.map((doc) => (
                          <div key={doc.id} onClick={() => handleDocumentClick(doc.url)}>
                            <DocumentIcon />
                            <span>{doc.name}</span>
                          </div>
                        ))
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  <div className="item notes driver-notes">
                    <div className="header">
                      <h3>DRIVER NOTES</h3>
                    </div>
                    <div className="content">
                      {routePickup?.driverNotes ? (
                        <div>{routePickup?.driverNotes}</div>
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  <div className="item barcode-scanner">
                    <div className="header">
                      <h3>BARCODE SCANNER</h3>
                    </div>
                    <div className="content">
                      {validationsLoading ? (
                        <div>Loading validation settings...</div>
                      ) : (
                        <>
                          <div
                            className={`barcode-scanner-status ${pickupBarcodeStatus.className}`}
                          >
                            {pickupBarcodeStatus.text}
                          </div>
                          {pickupBarcodes.length > 0 && (
                            <div className="barcode-details">
                              {pickupBarcodes.map((pack, i) => (
                                <div className="package" key={pack.id}>
                                  {pack.name} x {pack.quantity}: {pack.scanned || 0}/
                                  {pack.quantity}
                                </div>
                              ))}
                            </div>
                          )}
                          {pickupBarcodes.length === 0 &&
                            (pickupBarcodeStatus.status === "required" ||
                              pickupBarcodeStatus.status === "pending") && (
                              <div className="barcode-requirement">
                                Barcode scanning is required for this pickup.
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </Col>
                <Col span="12">
                  <div className="item pod">
                    <div className="header">
                      <h3>POD {routeDropoff.proofs2?.length > 0 && "1"}</h3>
                    </div>
                    <div className="content">
                      {routeDropoff.proofs?.length > 0 ? (
                        routeDropoff.proofs.map((proof) => (
                          <Image key={proof?.id} width={100} src={proof?.url} />
                        ))
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  {routeDropoff.proofs2?.length > 0 && (
                    <div className="item pod">
                      <div className="header">
                        <h3>POD 2</h3>
                      </div>
                      <div className="content">
                        {routeDropoff.proofs2.map((proof) => (
                          <Image key={proof?.id} width={100} src={proof?.url} />
                        ))}
                      </div>
                    </div>
                  )}
                  {sftpPodEntitlementActive &&
                    job?.status === "completed" &&
                    sftpPodLog !== undefined && (
                      <div className="item ftp-pod-status">
                        <div className="header">
                          <h3>FTP UPLOAD</h3>
                        </div>
                        <div className="content">
                          {sftpPodLog === null ? (
                            <span className="empty secondary-text">
                              No upload attempt recorded.
                            </span>
                          ) : (
                            <Tooltip
                              title={`${
                                sftpPodLog.success ? "Success" : "Failed"
                              } · ${moment(sftpPodLog.createdAt).format(
                                DATETIME_FORMAT_FRONTEND
                              )}${
                                sftpPodLog.errorMessage
                                  ? ` · ${sftpPodLog.errorMessage}`
                                  : ""
                              }`}
                            >
                              <img
                                src={
                                  sftpPodLog.success
                                    ? "/icons/ftp-success.webp"
                                    : "/icons/ftp-fail.webp"
                                }
                                alt={
                                  sftpPodLog.success
                                    ? "FTP Upload Success"
                                    : "FTP Upload Failed"
                                }
                                style={{ width: 40, cursor: "pointer" }}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    )}
                  <div className="item signature">
                    <div className="header">
                      <h3>SIGNATURE {routeDropoff.signaturePhoto2 && "1"}</h3>
                    </div>
                    <div className="content">
                      {routeDropoff?.signaturePhoto ? (
                        <>
                          <img src={routeDropoff.signaturePhoto.url} />
                          <div>
                            Signature Name:{" "}
                            {routeDropoff?.senderName || "(No name was provided)"}
                          </div>
                        </>
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  {routeDropoff?.signaturePhoto2 && (
                    <div className="item signature">
                      <div className="header">
                        <h3>SIGNATURE 2</h3>
                      </div>
                      <div className="content">
                        <img src={routeDropoff.signaturePhoto2.url} />
                        <div>
                          Signature Name 2:{" "}
                          {routeDropoff?.senderName2 || "(No name was provided)"}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="item notes customer-notes">
                    <div className="header">
                      <h3>CUSTOMER NOTES</h3>
                    </div>
                    <div className="content">
                      {routeDropoff?.location?.note ? (
                        <div>{routeDropoff?.location?.note}</div>
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  <div className="item document-scanner">
                    <div className="header">
                      <h3>DOCUMENT SCANNER</h3>
                    </div>
                    <div className="content">
                      {loading ? (
                        <div>Loading documents...</div>
                      ) : dropoffDocuments.length > 0 ? (
                        dropoffDocuments.map((doc) => (
                          <div key={doc.id} onClick={() => handleDocumentClick(doc.url)}>
                            <DocumentIcon />
                            <span>{doc.name}</span>
                          </div>
                        ))
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  <div className="item notes driver-notes">
                    <div className="header">
                      <h3>DRIVER NOTES</h3>
                    </div>
                    <div className="content">
                      {routeDropoff?.driverNotes ? (
                        <div>{routeDropoff?.driverNotes}</div>
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                  <div className="item barcode-scanner">
                    <div className="header">
                      <h3>BARCODE SCANNER</h3>
                    </div>
                    <div className="content">
                      {validationsLoading ? (
                        <div>Loading validation settings...</div>
                      ) : (
                        <>
                          <div
                            className={`barcode-scanner-status ${dropoffBarcodeStatus.className}`}
                          >
                            {dropoffBarcodeStatus.text}
                          </div>
                          {dropoffBarcodes.length > 0 && (
                            <div className="barcode-details">
                              {dropoffBarcodes.map((pack, i) => (
                                <div className="package" key={pack.id}>
                                  {pack.name} x {pack.quantity}: {pack.scanned || 0}/
                                  {pack.quantity}
                                </div>
                              ))}
                            </div>
                          )}
                          {dropoffBarcodes.length === 0 &&
                            (dropoffBarcodeStatus.status === "required" ||
                              dropoffBarcodeStatus.status === "pending") && (
                              <div className="barcode-requirement">
                                Barcode scanning is required for this delivery.
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="item cod-receive">
                    <div className="header">
                      <h3>COD</h3>
                    </div>
                    <div className="content">
                      {routeDropoff?.location?.cod ? (
                        <>
                          <div
                            className={`cod-receive-status ${
                              routeDropoff?.status === "completed"
                                ? "received"
                                : "pending"
                            }`}
                          >
                            {routeDropoff?.status === "completed"
                              ? "received"
                              : "pending"}
                          </div>
                          <div className="cod-receive-value">
                            $ {routeDropoff?.location?.cod}
                          </div>
                        </>
                      ) : (
                        <div className="empty secondary-text">Empty</div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
    </section>
  );
};
export default JobDetail;
